import {
  SkeletonLoader,
  Tooltip,
  TooltipTrigger,
  WithNAFallback,
} from '@toggle/design-system';
import { NumericalCellValueStyle } from '@toggle/helpers';
import { Entity, formatTicker, mapEntity } from '@toggle/toggle';
import React, { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { AssetLogoContainer } from '~/components/asset-logo-container/AssetLogoContainer';
import { PriceCellTooltip } from '~/components/price-cell/price-cell-tooltip/PriceCellTooltip';
import {
  getPriceStamp,
  isNonZeroReturn,
} from '~/components/price-display-view/helpers/price-display-helpers';
import { useNavigateToAssetOverview } from '~/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useOneDayReturnLivePrice } from '~/hooks/use-one-day-return-live-price/useOneDayReturnLivePrice';
import { mapValueTypeToFormatter } from '~/views/screener/results/screener-table-utils';
import { TopRankIcon } from '~/widgets/knowledge-graph-widget/components/top-rank-icon/TopRankIcon';

import styles from './CompetitorCard.module.scss';

const colors: Record<NumericalCellValueStyle, string> = {
  [NumericalCellValueStyle.Positive]: 'var(--viz-bullish)',
  [NumericalCellValueStyle.Negative]: 'var(--viz-bearish)',
  [NumericalCellValueStyle.Neutral]: 'var(--text-default)',
};

const backgroundColors: Record<NumericalCellValueStyle, string> = {
  [NumericalCellValueStyle.Positive]: 'var(--viz-bullish-soft)',
  [NumericalCellValueStyle.Negative]: 'var(--viz-bearish-soft)',
  [NumericalCellValueStyle.Neutral]: 'transparent',
};

interface CompetitorCardProps {
  entity: Entity;
  rank?: number;
  clickHandler?: () => void;
}

export const CompetitorCard = ({
  entity,
  rank,
  clickHandler,
}: CompetitorCardProps) => {
  const { t } = useTranslation(['widget']);
  const {
    oneDayLiveReturn,
    isSubscribing,
    date,
    isLive,
    isLivePriceSupported,
    isExtendedHours,
  } = useOneDayReturnLivePrice(mapEntity(entity));
  const stamp = getPriceStamp({
    isLivePriceSupported,
    isLive,
    isDisabled: false,
  });

  const { navigateToPage } = useNavigateToAssetOverview();

  const handleClick = () => {
    if (clickHandler) {
      clickHandler();
    } else {
      navigateToPage({ entityTag: entity.tag });
    }
  };

  const formatter = mapValueTypeToFormatter['float'];
  const isNegative = !!oneDayLiveReturn && oneDayLiveReturn < 0;
  const prefix = isNegative ? '' : '+';
  const numericalStyle =
    oneDayLiveReturn === undefined
      ? NumericalCellValueStyle.Neutral
      : isNegative
      ? NumericalCellValueStyle.Negative
      : NumericalCellValueStyle.Positive;

  if (isSubscribing) {
    return (
      <SkeletonLoader
        areas={[
          { styles: { width: '100%', height: '132px', borderRadius: '8px' } },
        ]}
      />
    );
  }

  return (
    <div
      className={styles.root}
      data-testid="competitor-card"
      onClick={handleClick}
    >
      <div className={styles.iconsWrapper}>
        <AssetLogoContainer entity={entity} />
        {rank && (
          <TopRankIcon
            rank={rank}
            iconSize={24}
            rankFontSize={10}
            rankTopPosition="28%"
          />
        )}
      </div>
      <div className={styles.flexColumn}>
        <div className={styles.titleLabel} data-testid={`${entity.tag}-label`}>
          {formatTicker(entity)}
        </div>
        <div className={styles.subtitleLabel}>{entity.name}</div>
      </div>
      <Tooltip
        isTouchDevice={false}
        trigger={TooltipTrigger.Hover}
        label={date && <PriceCellTooltip type={stamp.stampType} date={date} />}
        inPortal
        disabled={!date || !oneDayLiveReturn}
      >
        <div
          className={styles.changePill}
          style={
            {
              '--change-color': colors[numericalStyle],
              '--change-bg': backgroundColors[numericalStyle],
            } as CSSProperties
          }
        >
          <div className={styles.horizonLabel}>
            {t(
              'widget:assetOverview.knowledgeGraph.themeExposure.tabs.themeExposure.oneDay'
            )}
          </div>
          <div className={styles.changeLabel}>
            <WithNAFallback
              check={
                isExtendedHours
                  ? isNonZeroReturn(oneDayLiveReturn)
                  : oneDayLiveReturn !== undefined
              }
              value={
                oneDayLiveReturn !== undefined
                  ? `${prefix}${formatter(oneDayLiveReturn)}%`
                  : undefined
              }
            />
          </div>
        </div>
      </Tooltip>
    </div>
  );
};
